import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Popconfirm,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  ReconciliationOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { editTransaction, getMyProfile } from "../../../network/network";

import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { AddonName } from "../../../configs/constants";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import TaxCalculator from "../../../components/TaxCalculator";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

import QuantityStepper from "../../../components/QuantityStepper";
import TagOptionsModal from "../POS/components/TagOptionsModal";
const EditOrder = () => {
  const { authUser, appConfig, roundOff } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productsData, setProductsData] = useState([]);
  const [cart, setCart] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);

  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  let { orderId } = useParams();
  const { t } = useTranslation();
  const [editOrderForm] = Form.useForm();
  const { state } = useLocation();
  let navigate = useNavigate();

  const is_stock_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );

  const getAvailableTags = (product) => {
    if (
      Array.isArray(product?.available_tags) &&
      product.available_tags.length > 0
    ) {
      return product.available_tags;
    }

    if (Array.isArray(product?.tags)) {
      return product.tags;
    }

    return [];
  };

  const hasProductTags = (product) => getAvailableTags(product).length > 0;

  const tagIdsFrom = (tags = []) =>
    tags.map((tg) => (tg && tg.id !== undefined ? tg.id : tg));

  const hasTagId = (ids = [], id) =>
    ids.some((tagId) => String(tagId) === String(id));

  const makeVariantKey = (productId, tagIds = []) =>
    `${productId}_${(tagIds || []).map(String).sort().join(",") || "no_tags"}`;

  useEffect(() => {
    state ? setData(state) : navigate("/orders/" + orderId);
  }, [navigate, orderId, state]);

  useEffect(() => {
    getMyProfile()
      .then((response) => {
        const resp = response.data || response.response?.data;
        if (resp && resp.success === true) {
          let products = [];
          (resp.data?.user?.categories || []).forEach((category) => {
            if (category.products && category.products.length > 0) {
              products = [...products, ...category.products];
            }
          });
          setProductsData(products);
        } else if (resp?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    editOrderForm.setFieldsValue({
      id: data.id,
      order_id: data.order_id,
    });
  }, [data, editOrderForm]);

  // Build cart from existing order items + product data
  useEffect(() => {
    if (data && Array.isArray(data.items) && data.items.length > 0) {
      const merged = data.items.map((item) => {
        const matching = productsData.find((p) => p.id === item.product_id);
        const available = matching ? getAvailableTags(matching) : [];

        const rawTagIds = Array.isArray(item.tags) ? tagIdsFrom(item.tags) : [];
        let normalizedTags = [];
        if (Array.isArray(item.tags) && item.tags.length > 0) {
          if (
            typeof item.tags[0] === "object" &&
            item.tags[0].id !== undefined
          ) {
            normalizedTags = item.tags;
          } else {
            normalizedTags = available.filter((at) =>
              hasTagId(rawTagIds, at.id),
            );
          }
        }

        // Existing DB line items get a stable unique key based on line id
        const baseVariantKey = makeVariantKey(item.product_id, rawTagIds);
        const cart_key = item.id ? `line_${item.id}` : baseVariantKey;

        return {
          ...item,
          is_taxable: matching?.is_taxable ?? item.is_taxable,
          is_stockable: matching?.is_stockable ?? item.is_stockable,
          stock: matching?.stock ?? item.stock,
          available_tags: available,
          tags: normalizedTags,
          selectedTagIds: rawTagIds,
          cart_key,
        };
      });

      setCart(JSON.parse(JSON.stringify(merged)));
    }
  }, [data, productsData]);

  // Recalculate taxes / totals
  useEffect(() => {
    let tax = 0;
    let total = 0;
    cart.forEach((product) => {
      const sub = Number(product.price) * Number(product.quantity || 0);
      let productTax = 0;
      if (
        (product.is_taxable || product.tax > 0) &&
        Array.isArray(product.taxes)
      ) {
        product.taxes.forEach((tx) => {
          productTax += sub * (Number(tx.rate) / 100);
        });
      }
      tax += productTax;
      total += sub + productTax;
    });
    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
  }, [cart, roundOff]);

  // Add new product to cart (from select)
  const addToCart = (productBase, tagIds = []) => {
    const available = getAvailableTags(productBase) || [];
    const selectedTags = available.filter((t) => hasTagId(tagIds, t.id));

    const normalizeIds = (ids = []) => ids.map(String).sort().join(",");
    const newProductId = Number(productBase.id);
    const newTagsKey = normalizeIds(tagIds);

    // find any existing line with same product + same tags
    const existingIndex = cart.findIndex((c) => {
      const cProductId = Number(c.product_id);
      const cTagsKey = normalizeIds(tagIdsFrom(c.tags || []));
      return cProductId === newProductId && cTagsKey === newTagsKey;
    });

    const newCart = [...cart];

    if (existingIndex === -1) {
      // stock check for *new* line
      if (is_stock_management_active && productBase.is_stockable) {
        const existingQtyTotal = newCart
          .filter((c) => Number(c.product_id) === newProductId)
          .reduce((s, it) => s + (it.quantity || 0), 0);
        if (existingQtyTotal >= productBase.stock) {
          showAppError(t("insufficient_quantity"));
          return;
        }
      }

      const newItem = {
        id: null, // new line, we only send product_id in payload
        product_id: productBase.id,
        name: productBase.name,
        name_en: productBase.name_en,
        image: productBase.image,
        price: productBase.price,
        taxes: productBase.taxes || [],
        is_taxable: productBase.is_taxable,
        is_stockable: productBase.is_stockable,
        stock: productBase.stock,
        available_tags: available,
        tags: selectedTags,
        quantity: 1,
        cart_key: makeVariantKey(productBase.id, tagIds),
      };
      newCart.push(newItem);
    } else {
      const target = newCart[existingIndex];

      // stock check for increment
      if (is_stock_management_active && target.is_stockable) {
        const existingQtyTotal = newCart
          .filter((c) => Number(c.product_id) === newProductId)
          .reduce((s, it) => s + (it.quantity || 0), 0);
        if (existingQtyTotal >= target.stock) {
          showAppError(t("insufficient_quantity"));
          return;
        }
      }

      newCart[existingIndex] = {
        ...target,
        quantity: (target.quantity || 0) + 1,
      };
    }

    setCart(newCart);
  };

  // Generic reducer for cart mutations
  const cartReducer = (payload, action) => {
    let newCart = [...cart];

    const findIndexByPayload = () => {
      if (payload.cart_key) {
        return newCart.findIndex((c) => c.cart_key === payload.cart_key);
      }
      if (payload.product_id && Array.isArray(payload.tags)) {
        const key = makeVariantKey(
          payload.product_id,
          tagIdsFrom(payload.tags),
        );
        return newCart.findIndex((c) => c.cart_key === key);
      }
      if (payload.product_id) {
        return newCart.findIndex(
          (c) => Number(c.product_id) === Number(payload.product_id),
        );
      }
      return -1;
    };

    const idx = findIndexByPayload();

    if (action === "inc") {
      if (idx !== -1) {
        if (is_stock_management_active && newCart[idx].is_stockable) {
          const existingQtyTotal = newCart
            .filter(
              (it) => Number(it.product_id) === Number(newCart[idx].product_id),
            )
            .reduce((s, it) => s + (it.quantity || 0), 0);
          if (existingQtyTotal >= newCart[idx].stock) {
            showAppError(t("insufficient_quantity"));
            return;
          }
        }

        newCart[idx] = { ...newCart[idx], quantity: newCart[idx].quantity + 1 };
      }
    } else if (action === "dec") {
      if (idx !== -1) {
        const newQty = (newCart[idx].quantity || 0) - 1;
        if (newQty <= 0) newCart.splice(idx, 1);
        else newCart[idx] = { ...newCart[idx], quantity: newQty };
      }
    } else if (action === "remove") {
      if (idx !== -1) newCart.splice(idx, 1);
    } else if (action === "update_options") {
      const currentIndex = newCart.findIndex(
        (c) => c.cart_key === payload.cart_key,
      );
      if (currentIndex === -1) {
        setCart(newCart);
        return;
      }

      const currentItem = newCart[currentIndex];
      const newTagIds = Array.isArray(payload.selectedTagIds)
        ? payload.selectedTagIds
        : payload.selectedTagIds
          ? [payload.selectedTagIds]
          : [];

      // Existing DB rows: keep same cart_key (no merge / no new row)
      // New lines (id === null): can merge with same product+tags
      const newVariantKey = currentItem.id
        ? currentItem.cart_key
        : makeVariantKey(currentItem.product_id, newTagIds);

      const available = currentItem.available_tags || [];
      const newTagsObjs = available.filter((tg) => hasTagId(newTagIds, tg.id));

      let otherIndex = -1;
      if (!currentItem.id) {
        // allow merge only for new lines
        otherIndex = newCart.findIndex(
          (c, i) => c.cart_key === newVariantKey && i !== currentIndex,
        );
      }

      if (otherIndex !== -1) {
        // merge into existing NEW variant
        newCart[otherIndex] = {
          ...newCart[otherIndex],
          quantity:
            (newCart[otherIndex].quantity || 0) + (currentItem.quantity || 0),
        };
        newCart.splice(currentIndex, 1);
      } else {
        // just update current row
        newCart[currentIndex] = {
          ...currentItem,
          tags: newTagsObjs,
          selectedTagIds: newTagIds,
          cart_key: newVariantKey,
        };
      }
    }

    setCart(newCart);
  };

  const handleProductSelect = (value) => {
    const prod = productsData.find((p) => p.id === value);
    if (!prod) return;

    if (hasProductTags(prod)) {
      setModalMode("add");
      setSelectedProduct(prod);
      setSelectedTagIds([]);
      setTagModalVisible(true);
    } else {
      addToCart(prod, []);
    }

    editOrderForm.setFieldsValue({ product: "" });
  };

  const openEditTagModalForCartItem = (cartItem) => {
    setModalMode("edit");
    setSelectedProduct(cartItem);
    setSelectedTagIds(tagIdsFrom(cartItem.tags || []));
    setTagModalVisible(true);
  };

  const handleConfirmModal = () => {
    if (!selectedProduct) {
      setTagModalVisible(false);
      return;
    }

    if (modalMode === "add") {
      addToCart(selectedProduct, selectedTagIds);
    } else {
      cartReducer(
        {
          cart_key: selectedProduct.cart_key,
          selectedTagIds: selectedTagIds,
        },
        "update_options",
      );
    }

    setTagModalVisible(false);
    setSelectedProduct(null);
    setSelectedTagIds([]);
    setModalMode("add");
  };

  const SubtotalDisplay = ({ record }) => {
    let subTotal = Number(record.price) * Number(record.quantity || 0);
    if (record.is_taxable && Array.isArray(record.taxes)) {
      const totalTaxRate = record.taxes.reduce(
        (sum, tx) => sum + Number(tx.rate),
        0,
      );
      subTotal += subTotal * (totalTaxRate / 100);
    }
    return roundOff(subTotal);
  };

  const tableColumns = [
    {
      title: "#",
      className: "app-table-index-column",
      render: (text, record, index) => index + 1,
    },
    {
      title: t("image"),
      align: "center",
      width: 85,
      render: (record) => (
        <img
          alt={record.name_en}
          src={record.image ? record.image : "/images/image-not-found.png"}
          style={{
            width: 50,
            height: "auto",
            borderRadius: "1rem",
            border: "1px solid var(--color-border)",
          }}
        />
      ),
    },
    {
      title: t("product"),
      render: (record) =>
        appConfig.direction === "ltr" ? record.name_en : record.name,
    },
    {
      title: t("options"),
      align: "center",
      render: (record) =>
        (record.available_tags || []).length > 0 ? (
          <div>
            <Button
              icon={<TagsOutlined />}
              type="link"
              onClick={() => openEditTagModalForCartItem(record)}
            >
              {record.tags && record.tags.length > 0
                ? `${record.tags.length} ${t("selected")}`
                : t("select_options")}
            </Button>
            {record.tags && record.tags.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {record.tags.map((tg) => (
                  <span
                    key={tg.id ?? tg}
                    style={{
                      display: "inline-block",
                      marginRight: 6,
                      padding: "2px 8px",
                      background: "var(--color-bg)",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  >
                    {appConfig.direction === "rtl"
                      ? tg.name_ar || tg.name
                      : tg.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: t("unit_price"),
      render: (record) => <CurrencyFormatter value={record.price} />,
    },
    {
      title: t("quantity"),
      render: (record) => (
        <QuantityStepper
          value={record.quantity}
          min={1}
          decreaseDisabled={record.quantity <= 1}
          increaseDisabled={
            record.is_stockable ? record.stock <= record.quantity : false
          }
          onDecrease={() => cartReducer({ cart_key: record.cart_key }, "dec")}
          onIncrease={() => {
            if (record.is_stockable) {
              if (record.stock > record.quantity)
                cartReducer({ cart_key: record.cart_key }, "inc");
              else showAppError(t("insufficient_quantity"));
            } else {
              cartReducer({ cart_key: record.cart_key }, "inc");
            }
          }}
        />
      ),
    },
    {
      title: t("price_including_tax"),
      render: (record) => (
        <CurrencyFormatter value={SubtotalDisplay({ record })} />
      ),
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => (
        <Popconfirm
          title={t("warning_delete")}
          onConfirm={() => cartReducer({ cart_key: record.cart_key }, "remove")}
          okText={t("yes")}
          cancelText={t("no")}
          placement="topLeft"
        >
          <Button type="link" title={t("delete_entity")}>
            <DeleteOutlined />
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/orders",
      breadcrumbName: t("orders"),
      icon: <ReconciliationOutlined />,
    },
    {
      path: "edit-orders",
      breadcrumbName: t("edit_order"),
      icon: <EditOutlined />,
    },
  ];

  const handleEditTransaction = () => {
    editOrderForm
      .validateFields()
      .then((values) => {
        setLoading(true);

        // API expects id as product_id
        values.items = JSON.stringify(
          cart.map((item) => ({
            id: item.product_id,
            qty: item.quantity,
            price: item.price,
            tags:
              item.tags && item.tags.length > 0
                ? item.tags.map((t) => t.id ?? t)
                : Array.isArray(item.selectedTagIds)
                  ? item.selectedTagIds
                  : [],
          })),
        );

        editTransaction(values)
          .then((response) => {
            const resp = response.data || response.response?.data;
            if (resp && resp.success === true) {
              NotificationWithIcon(
                "success",
                t("order_updated"),
                t("order_updated_successfully"),
              );
              navigate("/orders/" + orderId);
            } else if (resp?.success === false) {
              handleApiError(response, t);
            }
          })
          .catch((err) => handleApiError(err, t))
          .finally(() => setLoading(false));
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <div className="app-relative">
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("edit_order")}
          onBack={() => window.history.back()}
          ghost={false}
          className="app-p-0"
        />
      </Card>

      <Spin spinning={loading}>
        <Card
          style={{
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          {data && (
            <Form
              name="edit-order-form"
              layout="vertical"
              onFinish={handleEditTransaction}
              form={editOrderForm}
            >
              <Form.Item name="id" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Row gutter={[20, 20]}>
                <Col xs={24} md={12}>
                  <Form.Item name="order_id" label={<b>{t("order_id")}</b>}>
                    <Input size="large" disabled />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="product"
                    label={<b>{t("product_add")}</b>}
                    rules={[
                      {
                        required: cart.length < 1,
                        message: t("validate_product_name_required"),
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      allowClear
                      options={productsData.map((p) => ({
                        disabled:
                          is_stock_management_active && p.is_stockable
                            ? p.stock <= 0
                            : false,
                        label:
                          appConfig.direction === "ltr"
                            ? `${p.name_en} (${p.category})`
                            : `${p.name} (${p.category_ar})`,
                        value: p.id,
                      }))}
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      size="large"
                      onChange={handleProductSelect}
                    />
                  </Form.Item>
                </Col>

                {cart.length > 0 && (
                  <Col xs={24}>
                    <Table
                      columns={tableColumns}
                      dataSource={cart}
                      pagination={false}
                      rowKey={(rec) => rec.cart_key}
                    />
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        marginTop: 30,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: 10,
                          borderBottom: "1px solid var(--color-border)",
                        }}
                      >
                        {t("total_without_tax")}
                        <span>
                          <CurrencyFormatter value={totalAmount - totalTax} />
                        </span>
                      </div>

                      {totalTax > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: 10,
                            borderBottom: "1px solid var(--color-border)",
                          }}
                        >
                          <div>
                            {t("total_taxes")}
                            <Popover
                              content={<TaxCalculator data={cart} />}
                              trigger="click"
                            >
                              <InfoCircleOutlined
                                style={{
                                  fontSize: 16,
                                  color: "var(--color-primary-hover)",
                                  margin: "0 5px",
                                }}
                              />
                            </Popover>
                          </div>
                          <span>
                            <CurrencyFormatter value={totalTax} />
                          </span>
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: 10,
                          color: "var(--color-primary-hover)",
                        }}
                      >
                        {authUser.is_vat_exempt
                          ? t("total")
                          : t("total_with_tax")}
                        <b>
                          <CurrencyFormatter value={totalAmount} />
                        </b>
                      </div>
                    </div>
                  </Col>
                )}

                <Col xs={24}>
                  <Form.Item className="app-text-center">
                    <Space>
                      <Button
                        type="default"
                        onClick={() => navigate("/orders/" + orderId)}
                      >
                        {t("cancel")}
                      </Button>
                      <Button type="primary" htmlType="submit">
                        {t("submit")}
                      </Button>
                    </Space>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          )}
        </Card>
      </Spin>

      <TagOptionsModal
        t={t}
        appConfig={appConfig}
        open={tagModalVisible}
        onClose={() => {
          setTagModalVisible(false);
          setSelectedProduct(null);
          setSelectedTagIds([]);
          setModalMode("add");
        }}
        product={selectedProduct}
        options={getAvailableTags(selectedProduct)}
        selected={selectedTagIds}
        onChangeSelected={setSelectedTagIds}
        onConfirm={handleConfirmModal}
        title={t("select_options")}
        confirmText={selectedTagIds.length > 0 ? t("add") : t("skip")}
        cancelText={t("cancel")}
      />
    </div>
  );
};

export default EditOrder;
